import React, { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { toast } from 'sonner'
import { Card, Button, Input } from '../../components/UI'
import { FileSignature, Download, Eye, ChevronDown, Building2, User, FileText } from 'lucide-react'

// ============================================
// Datos legales pre-redactados
// ============================================

const CLAUSES = [
    {
        title: '1. Objeto del contrato',
        content: `El presente contrato tiene por objeto regular la prestación de servicios de monitorización, telemetría y control remoto de aplicaciones web ("Torre de Control") por parte del PRESTADOR al CLIENTE.

El servicio incluye:
• Validación de licencias de software en tiempo real
• Recopilación de telemetría de uso (sesiones, pageviews, errores, performance)
• Envío de comandos remotos seguros (firmados con HMAC)
• Panel de administración para gestión de licencias y features`
    },
    {
        title: '2. Duración y vigencia',
        content: `El contrato entrará en vigor en la fecha de firma y tendrá una duración determinada por la licencia asociada.

La licencia incluye un período de gracia configurable (grace_days) tras su expiración, durante el cual:
• Se notifica al administrador mediante aviso discreto
• La aplicación entra en modo "solo lectura"
• En ningún caso se produce un bloqueo inmediato total

Transcurrido el período de gracia sin renovación, el servicio quedará suspendido.`
    },
    {
        title: '3. Protección de datos (RGPD)',
        content: `De conformidad con el Reglamento (UE) 2016/679 (RGPD):

3.1 MINIMIZACIÓN (Art. 5.1.c): El sistema recopila exclusivamente los datos necesarios para la prestación del servicio. No se capturan: User-Agent completo, resolución de pantalla, timezone, URLs con querystrings.

3.2 BASE LEGAL (Art. 6): El tratamiento se fundamenta en la ejecución del presente contrato (Art. 6.1.b) y el interés legítimo del PRESTADOR (Art. 6.1.f) para la prevención de fraude y control de licencias.

3.3 SEGURIDAD (Art. 32): Los datos se transmiten cifrados (HTTPS/TLS), se almacenan con Row Level Security (RLS) en base de datos, y los comandos remotos se firman digitalmente con HMAC-SHA256.

3.4 DERECHOS DEL INTERESADO (Arts. 15-22): El CLIENTE y sus usuarios tienen derecho de acceso, rectificación, supresión, portabilidad y oposición. El PRESTADOR facilitará el ejercicio de estos derechos mediante el Dashboard de Transparencia.

3.5 SANITIZACIÓN: Todos los datos de error (stack traces, URLs) se sanitizan antes del envío, eliminando automáticamente tokens, emails, JWTs y rutas con información personal.`
    },
    {
        title: '4. Nivel de servicio (SLA)',
        content: `El PRESTADOR se compromete a:
• Disponibilidad del servicio de validación: 99.5% mensual
• Tiempo máximo de respuesta a incidencias críticas: 4 horas
• Notificación previa de mantenimientos programados: 24 horas
• Backup de datos de telemetría: retención mínima de 90 días

Exclusiones:
• Interrupciones causadas por el proveedor de infraestructura (Supabase, Cloudflare)
• Uso indebido o modificación del código del agente (vendor_core) por parte del CLIENTE`
    },
    {
        title: '5. Responsabilidades del cliente',
        content: `El CLIENTE se compromete a:
• No modificar, descompilar ni realizar ingeniería inversa del agente vendor_core
• Mantener las credenciales (VITE_DRM_CLIENT_ID) de forma confidencial
• No compartir la licencia con terceros no autorizados
• Informar a sus usuarios finales sobre la existencia del sistema de telemetría
• No exceder el número máximo de usuarios concurrentes definido en la licencia`
    },
    {
        title: '6. Propiedad intelectual',
        content: `El código fuente del sistema Torre de Control, incluyendo el agente vendor_core (en sus versiones JavaScript y TypeScript), los scripts de ofuscación, las RPCs de base de datos y el panel de administración, es propiedad exclusiva del PRESTADOR.

La licencia otorgada al CLIENTE es de uso, no transferible y limitada al dominio o dominios especificados en la configuración de la licencia.`
    },
    {
        title: '7. Resolución del contrato',
        content: `El contrato podrá resolverse por:
• Expiración de la licencia sin renovación (tras el período de gracia)
• Incumplimiento de las obligaciones por cualquiera de las partes
• Mutuo acuerdo
• Revocación administrativa de la licencia desde el panel de control

En caso de resolución, el PRESTADOR:
• Cesará la recopilación de telemetría de forma inmediata
• Proporcionará al CLIENTE una exportación de sus datos (JSON, Art. 20 RGPD)
• Eliminará los datos del CLIENTE en un plazo máximo de 30 días`
    },
]

// ============================================
// Componente Principal
// ============================================

interface LicenseOption {
    id: string
    client_name: string
    client_id: string
    allowed_domains: string[]
    projects?: { name: string }
}

interface PartyData {
    company: string
    cif: string
    representative: string
    address: string
    email: string
}

export default function ContractsPage() {
    const [licenses, setLicenses] = useState<LicenseOption[]>([])
    const [selectedLicense, setSelectedLicense] = useState<string>('')
    const [loading, setLoading] = useState(true)
    const [previewing, setPreviewing] = useState(false)

    const [provider, setProvider] = useState<PartyData>({
        company: 'Antigravity Digital S.L.',
        cif: '',
        representative: '',
        address: '',
        email: '',
    })

    const [client, setClient] = useState<PartyData>({
        company: '',
        cif: '',
        representative: '',
        address: '',
        email: '',
    })

    useEffect(() => { fetchLicenses() }, [])

    const fetchLicenses = async () => {
        try {
            const { data, error } = await supabase
                .from('licenses')
                .select('id, client_name, client_id, allowed_domains, projects(name)')
                .order('client_name')
            if (error) throw error
            setLicenses(data || [])
        } catch {
            toast.error('Error cargando licencias')
        } finally {
            setLoading(false)
        }
    }

    const handleLicenseSelect = (id: string) => {
        setSelectedLicense(id)
        const lic = licenses.find(l => l.id === id)
        if (lic) {
            setClient(prev => ({
                ...prev,
                company: lic.client_name || '',
            }))
        }
    }

    const today = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })
    const selectedLic = licenses.find(l => l.id === selectedLicense)

    const generateHTML = () => {
        return `<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <title>Contrato de Prestación de Servicios — Torre de Control</title>
    <style>
        body { font-family: 'Segoe UI', system-ui, sans-serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #1a1a1a; line-height: 1.6; }
        h1 { text-align: center; font-size: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
        h2 { font-size: 14px; margin-top: 30px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
        .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin: 20px 0; }
        .party { padding: 15px; border: 1px solid #ddd; border-radius: 8px; }
        .party h3 { margin: 0 0 10px 0; font-size: 13px; color: #666; text-transform: uppercase; }
        .party p { margin: 2px 0; font-size: 13px; }
        .clause { margin-bottom: 20px; }
        .clause h2 { font-size: 14px; }
        .clause pre { white-space: pre-wrap; font-family: inherit; font-size: 13px; margin: 0; }
        .license-info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0; font-size: 13px; }
        .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 60px; }
        .sig-block { border-top: 1px solid #333; padding-top: 10px; text-align: center; font-size: 12px; }
        .footer { text-align: center; font-size: 11px; color: #999; margin-top: 40px; border-top: 1px solid #eee; padding-top: 10px; }
        @media print { body { margin: 0; } }
    </style>
</head>
<body>
    <h1>CONTRATO DE PRESTACIÓN DE SERVICIOS<br><small style="font-weight:normal;font-size:14px">Sistema Torre de Control — Antigravity</small></h1>

    <p style="text-align:center;color:#666;font-size:13px">Fecha: ${today}</p>

    <div class="parties">
        <div class="party">
            <h3>Prestador del Servicio</h3>
            <p><strong>${provider.company}</strong></p>
            ${provider.cif ? `<p>CIF: ${provider.cif}</p>` : ''}
            ${provider.representative ? `<p>Representante: ${provider.representative}</p>` : ''}
            ${provider.address ? `<p>Dirección: ${provider.address}</p>` : ''}
            ${provider.email ? `<p>Email: ${provider.email}</p>` : ''}
        </div>
        <div class="party">
            <h3>Cliente</h3>
            <p><strong>${client.company}</strong></p>
            ${client.cif ? `<p>CIF: ${client.cif}</p>` : ''}
            ${client.representative ? `<p>Representante: ${client.representative}</p>` : ''}
            ${client.address ? `<p>Dirección: ${client.address}</p>` : ''}
            ${client.email ? `<p>Email: ${client.email}</p>` : ''}
        </div>
    </div>

    ${selectedLic ? `
    <div class="license-info">
        <strong>Datos de la Licencia:</strong><br>
        Proyecto: ${selectedLic.projects?.name || 'N/A'}<br>
        Client ID: <code>${selectedLic.client_id}</code><br>
        Dominios: ${selectedLic.allowed_domains?.join(', ') || 'N/A'}
    </div>
    ` : ''}

    ${CLAUSES.map(c => `
    <div class="clause">
        <h2>${c.title}</h2>
        <pre>${c.content}</pre>
    </div>
    `).join('')}

    <div class="signatures">
        <div class="sig-block">
            <p>Fdo.: ${provider.representative || provider.company}</p>
            <p style="color:#999">PRESTADOR</p>
        </div>
        <div class="sig-block">
            <p>Fdo.: ${client.representative || client.company}</p>
            <p style="color:#999">CLIENTE</p>
        </div>
    </div>

    <div class="footer">
        Generado por Torre de Control v4.0.0 — ${today}
    </div>
</body>
</html>`
    }

    const downloadHTML = () => {
        const html = generateHTML()
        const blob = new Blob([html], { type: 'text/html' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `contrato_${client.company?.replace(/\s+/g, '_') || 'cliente'}_${new Date().toISOString().split('T')[0]}.html`
        a.click()
        URL.revokeObjectURL(url)
        toast.success('Contrato HTML descargado')
    }

    const downloadPDF = () => {
        const html = generateHTML()
        const printWindow = window.open('', '_blank')
        if (printWindow) {
            printWindow.document.write(html)
            printWindow.document.close()
            setTimeout(() => {
                printWindow.print()
            }, 500)
        }
        toast.success('Usa "Guardar como PDF" en el diálogo de impresión')
    }

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <FileSignature className="text-neon-blue" /> Contratos
                </h1>
                <p className="text-gray-500 text-sm">Generador de contratos de prestación de servicios</p>
            </div>

            {/* Selector de Licencia */}
            <Card className="mb-6">
                <label className="text-xs text-gray-400 block mb-1.5">Seleccionar licencia</label>
                <div className="relative">
                    <select
                        value={selectedLicense}
                        onChange={e => handleLicenseSelect(e.target.value)}
                        className="w-full bg-charcoal border border-white/10 rounded-lg px-4 py-3 text-white appearance-none focus:border-neon-blue focus:ring-1 focus:ring-neon-blue outline-none transition-all"
                    >
                        <option value="">— Seleccionar licencia —</option>
                        {licenses.map(l => (
                            <option key={l.id} value={l.id}>
                                {l.client_name} — {l.projects?.name || 'Sin proyecto'}
                            </option>
                        ))}
                    </select>
                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
            </Card>

            {/* Formulario de partes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Prestador */}
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                        <Building2 size={16} className="text-neon-blue" /> Prestador del Servicio
                    </h2>
                    <div className="space-y-3">
                        <Input label="Empresa" value={provider.company} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProvider({ ...provider, company: e.target.value })} />
                        <Input label="CIF / NIF" value={provider.cif} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProvider({ ...provider, cif: e.target.value })} placeholder="B12345678" />
                        <Input label="Representante" value={provider.representative} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProvider({ ...provider, representative: e.target.value })} />
                        <Input label="Dirección" value={provider.address} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProvider({ ...provider, address: e.target.value })} />
                        <Input label="Email" type="email" value={provider.email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProvider({ ...provider, email: e.target.value })} />
                    </div>
                </Card>

                {/* Cliente */}
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                        <User size={16} className="text-neon-green" /> Cliente
                    </h2>
                    <div className="space-y-3">
                        <Input label="Empresa" value={client.company} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClient({ ...client, company: e.target.value })} />
                        <Input label="CIF / NIF" value={client.cif} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClient({ ...client, cif: e.target.value })} placeholder="B12345678" />
                        <Input label="Representante" value={client.representative} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClient({ ...client, representative: e.target.value })} />
                        <Input label="Dirección" value={client.address} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClient({ ...client, address: e.target.value })} />
                        <Input label="Email" type="email" value={client.email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClient({ ...client, email: e.target.value })} />
                    </div>
                </Card>
            </div>

            {/* Preview de cláusulas */}
            <Card className="mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-white font-bold text-sm flex items-center gap-2">
                        <FileText size={16} /> Cláusulas del Contrato
                    </h2>
                    <span className="text-xs text-gray-500">{CLAUSES.length} cláusulas</span>
                </div>
                <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                    {CLAUSES.map((clause, i) => (
                        <div key={i} className="p-4 bg-black/20 rounded-lg border border-white/5">
                            <h3 className="text-neon-blue text-sm font-bold mb-2">{clause.title}</h3>
                            <pre className="text-gray-400 text-xs whitespace-pre-wrap font-sans leading-relaxed">{clause.content}</pre>
                        </div>
                    ))}
                </div>
            </Card>

            {/* Acciones */}
            <div className="flex flex-col sm:flex-row gap-3">
                <button
                    onClick={() => setPreviewing(true)}
                    className="flex-1 px-6 py-3.5 bg-white/5 hover:bg-white/10 text-white rounded-xl border border-white/10 transition-all flex items-center justify-center gap-2 font-medium"
                >
                    <Eye size={16} /> Previsualizar
                </button>
                <button
                    onClick={downloadHTML}
                    className="flex-1 px-6 py-3.5 bg-neon-blue/10 hover:bg-neon-blue/20 text-neon-blue rounded-xl border border-neon-blue/20 transition-all flex items-center justify-center gap-2 font-medium"
                >
                    <Download size={16} /> Descargar HTML
                </button>
                <button
                    onClick={downloadPDF}
                    className="flex-1 px-6 py-3.5 bg-gradient-to-r from-neon-blue to-indigo-600 text-white rounded-xl shadow-lg shadow-neon-blue/20 hover:opacity-90 transition-all flex items-center justify-center gap-2 font-medium"
                >
                    <FileSignature size={16} /> Exportar PDF
                </button>
            </div>

            {/* Modal Preview */}
            {previewing && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="w-full max-w-4xl bg-white rounded-xl flex flex-col max-h-[90vh] shadow-2xl relative">
                        <div className="p-4 border-b flex justify-between items-center shrink-0 bg-gray-50 rounded-t-xl">
                            <h2 className="text-gray-800 font-bold">Previsualización del Contrato</h2>
                            <button onClick={() => setPreviewing(false)} className="p-2 text-gray-500 hover:text-black rounded-full hover:bg-gray-200 transition-colors">✕</button>
                        </div>
                        <iframe
                            srcDoc={generateHTML()}
                            className="flex-1 w-full border-0 rounded-b-xl"
                            title="Preview del contrato"
                        />
                    </div>
                </div>
            )}
        </div>
    )
}
